import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { DocumentsPanel } from "@/components/common/DocumentsPanel";
import { label } from "@/lib/labels";
import { fmtDate, fmtShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Navigation, Phone, Boxes, Truck, AlertTriangle, ClipboardList, FileText, User } from "lucide-react";
import { TaskCard, mapsUrl } from "./FieldHome";

export default function FieldProject() {
  const { id } = useParams();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["project", id], queryFn: () => get(`/projects/${id}`) });
  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const p = q.data;
  const today = new Date().toISOString().slice(0, 10);
  const openTasks = (p.tasks || []).filter((t) => !["fullfort", "avbrutt"].includes(t.status)).map((t) => ({ ...t, project_name: p.name, overdue: t.due_date && t.due_date < today }));
  const allocs = (p.allocations || []).filter((a) => a.status !== "kansellert");
  const transports = (p.transports || []).filter((t) => t.status !== "fullfort");
  const issues = (p.issues || []).filter((i) => ["ny", "under_behandling"].includes(i.status));

  return (
    <div className="space-y-4 fade-up" data-testid="field-project-page">
      <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="field-back-button"><ArrowLeft className="h-4 w-4" /> Tilbake</button>
      <div className="rounded-2xl border bg-card p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0"><div className="text-xs text-muted-foreground font-mono">{p.project_number}</div><h1 className="text-lg font-display font-semibold leading-snug">{p.name}</h1><div className="text-sm text-muted-foreground">{p.customer?.name}</div></div>
          <StatusBadge status={p.status} />
        </div>
        <div className="text-sm">{p.address}</div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><div className="text-muted-foreground">Periode</div><div className="font-medium tabular">{fmtShort(p.start_date)} – {fmtShort(p.end_date)}</div></div>
          <div><div className="text-muted-foreground">Prosjektleder</div><div className="font-medium">{p.manager_name || "–"}</div></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button asChild className="h-11"><a href={mapsUrl(p.address, p.lat, p.lng)} target="_blank" rel="noreferrer" data-testid="field-project-navigate-button"><Navigation className="h-4 w-4 mr-1.5" /> Naviger</a></Button>
          {p.contact?.phone ? <Button asChild variant="outline" className="h-11"><a href={`tel:${p.contact.phone}`} data-testid="field-project-call-button"><Phone className="h-4 w-4 mr-1.5" /> Ring kontakt</a></Button> : <Button variant="outline" className="h-11" disabled><Phone className="h-4 w-4 mr-1.5" /> Ingen kontakt</Button>}
        </div>
        {p.contact && <div className="flex items-center gap-2 text-xs text-muted-foreground"><User className="h-3.5 w-3.5" /> {p.contact.name}{p.contact.title ? ` · ${p.contact.title}` : ""}</div>}
        {p.site_notes && <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900"><span className="font-medium">Merknad tomt:</span> {p.site_notes}</div>}
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-display font-semibold flex items-center gap-2 pt-1"><ClipboardList className="h-4 w-4 text-primary" /> Åpne oppgaver <span className="text-muted-foreground font-normal tabular">({openTasks.length})</span></h2>
        {openTasks.length === 0 ? <div className="text-sm text-muted-foreground px-1">Ingen åpne oppgaver.</div> : openTasks.map((t) => <TaskCard key={t.id} t={t} />)}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-display font-semibold flex items-center gap-2 pt-1"><Boxes className="h-4 w-4 text-primary" /> Moduler på prosjektet <span className="text-muted-foreground font-normal tabular">({allocs.length})</span></h2>
        {allocs.length === 0 ? <div className="text-sm text-muted-foreground px-1">Ingen moduler allokert.</div> : (
          <div className="rounded-2xl border bg-card divide-y" data-testid="field-project-modules">
            {allocs.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm">
                <div className="min-w-0"><span className="font-mono text-xs">{a.module_code}</span><span className="text-muted-foreground"> · {a.type_name}</span></div>
                <div className="flex items-center gap-1.5">{a.position && <span className="text-[11px] text-muted-foreground">{a.position}</span>}<StatusBadge status={a.module_status || a.status} /></div>
              </div>
            ))}
          </div>
        )}
      </section>

      {transports.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-display font-semibold flex items-center gap-2 pt-1"><Truck className="h-4 w-4 text-primary" /> Transporter <span className="text-muted-foreground font-normal tabular">({transports.length})</span></h2>
          {transports.map((t) => (
            <Link key={t.id} to={`/felt/transport?id=${t.id}`} className="block rounded-2xl border bg-card p-3.5 active:bg-muted/70" data-testid={`field-project-transport-${t.id}`}>
              <div className="flex items-start justify-between gap-2"><div><div className="font-medium">{t.transport_number} · {label(t.direction)}</div><div className="text-xs text-muted-foreground">{fmtDate(t.date)}{t.time_window ? ` · ${t.time_window}` : ""} · {(t.module_ids || []).length} moduler</div></div><StatusBadge status={t.status} /></div>
            </Link>
          ))}
        </section>
      )}

      {issues.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-display font-semibold flex items-center gap-2 pt-1"><AlertTriangle className="h-4 w-4 text-primary" /> Åpne avvik <span className="text-muted-foreground font-normal tabular">({issues.length})</span></h2>
          <div className="rounded-2xl border bg-card divide-y">
            {issues.map((i) => <div key={i.id} className="px-3 py-2.5 text-sm flex items-start justify-between gap-2"><div className="min-w-0"><div className="font-medium">{i.title}</div><div className="text-xs text-muted-foreground">{i.issue_number} · {fmtShort(i.created_at)}</div></div><StatusBadge status={i.severity} /></div>)}
          </div>
        </section>
      )}

      <section className="rounded-2xl border bg-card p-4 space-y-2">
        <h2 className="text-sm font-display font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Tegninger og dokumenter</h2>
        <DocumentsPanel entityType="project" entityId={id} compact />
      </section>

      <Button asChild variant="outline" className="w-full h-11 border-amber-300 text-amber-800 hover:bg-amber-50"><Link to={`/felt/avvik/ny?project_id=${id}`} data-testid="field-project-new-issue-button"><AlertTriangle className="h-4 w-4 mr-1.5" /> Registrer avvik på prosjektet</Link></Button>
    </div>
  );
}
